export const DIN_PAPER_SIZES = ["A5", "A4", "A3", "A2"] as const;

export type DinPaperSize = (typeof DIN_PAPER_SIZES)[number];
export type PaperOrientation = "portrait" | "landscape";

export type ClassifiedPaper = {
  size: DinPaperSize | null;
  orientation: PaperOrientation;
};

/** ISO 216 in mm, kurze × lange Seite. */
const DIN_MM: Record<DinPaperSize, readonly [number, number]> = {
  A5: [148, 210],
  A4: [210, 297],
  A3: [297, 420],
  A2: [420, 594],
};

const MM_PER_PT = 25.4 / 72;
/** ~8 % bzw. mind. 6 mm, damit CAD-/Scan-Exporte noch erkannt werden. */
const REL_TOLERANCE = 0.08;
const ABS_TOLERANCE_MM = 6;

export function isDinPaperSize(value: unknown): value is DinPaperSize {
  return DIN_PAPER_SIZES.includes(value as DinPaperSize);
}

export function formatDinPaperLabel(
  size: DinPaperSize | null,
  orientation?: PaperOrientation,
): string {
  if (!size) return "Unbekannt";
  const orient =
    orientation === "landscape" ? " quer" : orientation === "portrait" ? " hoch" : "";
  return `DIN ${size}${orient}`;
}

export function classifyPageMm(widthMm: number, heightMm: number): ClassifiedPaper {
  const width = Math.abs(Number(widthMm) || 0);
  const height = Math.abs(Number(heightMm) || 0);
  const orientation: PaperOrientation = width > height ? "landscape" : "portrait";
  const short = Math.min(width, height);
  const long = Math.max(width, height);
  if (short <= 0 || long <= 0) {
    return { size: null, orientation };
  }

  let best: DinPaperSize | null = null;
  let bestScore = Infinity;
  for (const size of DIN_PAPER_SIZES) {
    const [isoShort, isoLong] = DIN_MM[size];
    const shortTol = Math.max(isoShort * REL_TOLERANCE, ABS_TOLERANCE_MM);
    const longTol = Math.max(isoLong * REL_TOLERANCE, ABS_TOLERANCE_MM);
    const shortDelta = Math.abs(short - isoShort);
    const longDelta = Math.abs(long - isoLong);
    if (shortDelta <= shortTol && longDelta <= longTol) {
      const score = shortDelta / isoShort + longDelta / isoLong;
      if (score < bestScore) {
        best = size;
        bestScore = score;
      }
    }
  }
  return { size: best, orientation };
}

export function classifyPagePts(
  widthPt: number,
  heightPt: number,
  rotateDeg = 0,
): ClassifiedPaper {
  let width = Math.abs(Number(widthPt) || 0);
  let height = Math.abs(Number(heightPt) || 0);
  const rot = ((Math.round(rotateDeg) % 360) + 360) % 360;
  if (rot === 90 || rot === 270) {
    const swap = width;
    width = height;
    height = swap;
  }
  return classifyPageMm(width * MM_PER_PT, height * MM_PER_PT);
}

export function effectivePageSizes(
  detected: Array<DinPaperSize | null>,
  overrides: Record<number, DinPaperSize>,
): Array<DinPaperSize | null> {
  return detected.map((size, index) => overrides[index + 1] ?? size);
}
